import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const { refresh, user } = useAuth();
    
    const [status, setStatus] = useState("verifying"); // verifying, success, error
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        if (!token) {
            setStatus("error");
            setErrorMsg("Missing verification token.");
            return;
        }

        const verify = async () => {
            try {
                await api.get(`/auth/verify-email?token=${token}`);
                setStatus("success");
                toast.success("Email verified successfully!");
                // Refresh authentication status if user is currently logged in
                if (user) {
                    await refresh();
                }
            } catch (err) {
                setStatus("error");
                setErrorMsg(formatApiErrorDetail(err.response?.data?.detail) || "Invalid or expired verification token.");
                toast.error("Email verification failed.");
            }
        };

        verify();
    }, [token, refresh, user]);

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
            <div className="tf-card-accent w-full max-w-md p-8 border border-[#27272A] relative">
                <div className="text-center mt-4 mb-6">
                    <div className="label-mono text-[#00FF41] mb-2">// activation protocol</div>
                    <h2 className="heading-md">EMAIL VERIFICATION</h2>
                </div>

                {status === "verifying" && (
                    <div className="flex flex-col items-center justify-center py-6 space-y-4">
                        <Loader2 className="w-12 h-12 text-[#00FF41] animate-spin" />
                        <p className="font-mono text-sm text-[#A1A1AA] animate-pulse">Verifying token in database...</p>
                    </div>
                )}

                {status === "success" && (
                    <div className="space-y-6 text-center">
                        <div className="flex justify-center">
                            <CheckCircle2 className="w-16 h-16 text-[#00FF41]" />
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-mono text-[#FAFAFA]">VERIFIED</h3>
                            <p className="text-sm text-[#A1A1AA] font-mono leading-relaxed">
                                Your account is now fully activated. You can close this tab or proceed to your dashboard.
                            </p>
                        </div>
                        <Link to="/app/tunnels" className="tf-btn w-full justify-center mt-6">
                            PROCEED TO DASHBOARD
                        </Link>
                    </div>
                )}

                {status === "error" && (
                    <div className="space-y-6 text-center">
                        <div className="flex justify-center">
                            <XCircle className="w-16 h-16 text-[#EF4444]" />
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-mono text-[#EF4444]">VERIFICATION FAILED</h3>
                            <p className="text-sm text-[#A1A1AA] font-mono leading-relaxed">
                                {errorMsg}
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 mt-6">
                            <Link to="/login" className="tf-btn w-full justify-center">
                                GO TO LOGIN
                            </Link>
                            <Link to="/" className="text-xs text-[#52525B] hover:text-[#FAFAFA] font-mono mt-2">
                                Back to Homepage
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
